import { useEffect, useState } from "react";

import { useDebouncedValue } from "@/hooks/use-debounced-value";

/** One character is not a search — it matches a fifth of every title. */
export const MIN_SEARCH_LENGTH = 2;

/**
 * The two values a search dialog needs, in one place.
 *
 * `liveTerm` is what is in the field right now, and it is what **Enter** uses:
 * a deliberate submit must never be swallowed because the 300ms timer has not
 * fired yet (type "travel", press Enter inside the debounce window, and a
 * debounced-only submit navigates nowhere).
 *
 * `trimmed` is the settled value the queries run on, so typing is one request
 * per burst rather than one per character. `pending` is the gap between them,
 * and it is what dims the previous results instead of leaving them looking like
 * the answer to the new query.
 *
 * Both search dialogs share this hook so the debounce contract cannot drift
 * between them — the topics palette and the pronunciation palette must fire the
 * same number of requests for the same typing.
 */
export function useSearchTerm(open: boolean) {
  const [term, setTerm] = useState("");

  const debounced = useDebouncedValue(term, 300);
  const trimmed = debounced.trim();
  const liveTerm = term.trim();
  const pending = liveTerm !== trimmed;

  /** A query has actually been issued for this value. */
  const canSearch = trimmed.length >= MIN_SEARCH_LENGTH;
  /** Long enough to submit — deliberately based on the live value. */
  const canSubmit = liveTerm.length >= MIN_SEARCH_LENGTH;
  /** Typed, but not yet enough characters for either. */
  const typingTooShort = liveTerm.length > 0 && liveTerm.length < MIN_SEARCH_LENGTH;

  // A dialog opens on a fresh field.
  useEffect(() => {
    if (!open) setTerm("");
  }, [open]);

  return {
    term,
    setTerm,
    trimmed,
    liveTerm,
    pending,
    canSearch,
    canSubmit,
    typingTooShort,
  };
}
